import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useContent } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  accommodation: "",
  arrival_date: "",
  departure_date: "",
  guests: "",
  message: "",
};

const Contact = () => {
  const c = useContent();
  const phone = c("contact.phone", "07982 844201");
  const email = c("contact.email", "justina@walnutlakes.co.uk");
  const phoneDigits = String(phone).replace(/[^0-9+]/g, "");
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        guests: form.guests ? Number(form.guests) : undefined,
      };
      await axios.post(`${API}/enquiries`, payload);
      toast.success("Thanks — we'll be in touch shortly.");
      setForm(initial);
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please check the form.");
    } finally {
      setLoading(false);
    }
  };

  const fieldClass =
    "bg-[#F5EFE6] border-[#DDD1BE] focus-visible:ring-2 focus-visible:ring-[#0F0E0E] focus-visible:border-[#0F0E0E] rounded-lg h-11";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-36 bg-[#F5EFE6]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="overline text-[#6A5F58]">
              <span className="marker-line" />
              Enquire
            </p>
            <h2 className="font-serif text-4xl md:text-5xl mt-6 leading-[1.05] text-[#14100F]">
              Tell us when you’d like to come.
            </h2>
            <p className="mt-6 text-[#3A322E] text-base md:text-lg leading-relaxed">
              Send us a quick note about your dates and party size and we’ll
              come back to you with availability, prices and any questions.
              We’re a small team, so replies come from a real human — usually
              within 24 hours during the season.
            </p>

            <div className="mt-10 space-y-2 text-[#3A322E]">
              <div className="text-sm">
                <span className="overline text-[#6A5F58] block mb-1">Call</span>
                <a href={`tel:${phoneDigits}`} className="link-underline text-lg text-[#14100F]">
                  {phone}
                </a>
              </div>
              <div className="text-sm pt-4">
                <span className="overline text-[#6A5F58] block mb-1">Email</span>
                <a
                  href={`mailto:${email}`}
                  className="link-underline text-lg text-[#14100F]"
                >
                  {email}
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-white border border-[#DDD1BE] rounded-2xl p-8 md:p-10 shadow-sm"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                    Full name *
                  </Label>
                  <Input
                    id="name"
                    data-testid="contact-name"
                    className={`mt-2 ${fieldClass}`}
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Appleby"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-email"
                    className={`mt-2 ${fieldClass}`}
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jane@example.co.uk"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    data-testid="contact-phone"
                    className={`mt-2 ${fieldClass}`}
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <Label htmlFor="accommodation" className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                    Accommodation
                  </Label>
                  <Select
                    value={form.accommodation}
                    onValueChange={(v) => setForm((p) => ({ ...p, accommodation: v }))}
                  >
                    <SelectTrigger
                      id="accommodation"
                      data-testid="contact-accommodation"
                      className={`mt-2 ${fieldClass}`}
                    >
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lodge">Lakeside Lodge</SelectItem>
                      <SelectItem value="touring">Camping / Caravan</SelectItem>
                      <SelectItem value="tent">Tent Pitch</SelectItem>
                      <SelectItem value="unsure">Not sure yet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="arrival" className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                    Arrival
                  </Label>
                  <Input
                    id="arrival"
                    type="date"
                    data-testid="contact-arrival"
                    className={`mt-2 ${fieldClass}`}
                    value={form.arrival_date}
                    onChange={update("arrival_date")}
                  />
                </div>
                <div>
                  <Label htmlFor="departure" className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                    Departure
                  </Label>
                  <Input
                    id="departure"
                    type="date"
                    data-testid="contact-departure"
                    className={`mt-2 ${fieldClass}`}
                    value={form.departure_date}
                    onChange={update("departure_date")}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="guests" className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                    Guests
                  </Label>
                  <Input
                    id="guests"
                    type="number"
                    min="1"
                    max="10"
                    data-testid="contact-guests"
                    className={`mt-2 ${fieldClass}`}
                    value={form.guests}
                    onChange={update("guests")}
                    placeholder="Number of adults (15+)"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="message" className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="contact-message"
                    className="mt-2 bg-[#F5EFE6] border-[#DDD1BE] focus-visible:ring-2 focus-visible:ring-[#0F0E0E] focus-visible:border-[#0F0E0E] rounded-lg min-h-[130px]"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us a little about what you're looking for..."
                    required
                  />
                </div>
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-[#6A5F58]">
                  By sending you agree to be contacted about your enquiry.
                </p>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {loading ? "Sending..." : "Send enquiry"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
